import { GridApi } from "ag-grid-community";
import { message } from "antd";
import { ForwardedRef, MutableRefObject, useImperativeHandle } from "react";
import DeliverableService from "../../../../features/DeliverableView/services/DeliverableService";
import CoaService from "../../../../features/CoaView/services/CoaService";
import TemplateService from "../../../../features/TemplateView/services/TemplateService";
import { AgGridHandle } from "../AgGridContext";
import { currencyFormatter } from "../AgGridUtils";

interface UseAgGridActionsParams {
  ref: ForwardedRef<AgGridHandle>;
  gridApiRef: MutableRefObject<GridApi | null>;
  deliverableId: string | undefined;
  sheetId: string | undefined;
  pathname: string;
  activeTab: string | null;
  fetchData: () => Promise<void>;
  deliverableService: DeliverableService;
  coaService: CoaService;
  templateService: TemplateService;
  setIsEditMode: (v: boolean) => void;
  setSearchValue: (v: string) => void;
}

const createEmptyRows = (api: GridApi, count: number): Record<string, any>[] => {
  const emptyRow: Record<string, any> = {};
  api.getColumns()?.forEach((col) => {
    const field = col.getColDef().field;
    if (field) emptyRow[field] = "";
  });
  return Array.from({ length: count }, () => ({ ...emptyRow }));
};

export const useAgGridActions = ({
  ref,
  gridApiRef,
  deliverableId,
  sheetId,
  pathname,
  activeTab,
  fetchData,
  deliverableService,
  coaService,
  templateService,
  setIsEditMode,
  setSearchValue,
}: UseAgGridActionsParams) => {
  useImperativeHandle(ref, () => ({
    editOnline: () => {
      setIsEditMode(true);
      message.success("Edit mode enabled");
    },
    save: async () => {
      if (!gridApiRef.current) return;
      const api = gridApiRef.current;
      api.stopEditing();

      const filterModel = api.getFilterModel();
      const rowGroupCols = api.getRowGroupColumns().map((col) => col.getColId());
      api.setFilterModel(null);
      api.setRowGroupColumns([]);

      const blob = api.getDataAsExcel({
        allColumns: true,
        processCellCallback: (params) => {
          const colType = ((params.column.getColDef() as any).type || "").toLowerCase();
          if (colType === "data validation date") {
            if (!params.value) return "";
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return "";
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const dd = String(date.getDate()).padStart(2, "0");
            const yyyy = date.getFullYear();
            return `${mm}/${dd}/${yyyy}`;
          }
          if (colType === "data validation currency") {
            return currencyFormatter(params.value, "$");
          }
          return params.value;
        },
      });

      api.setFilterModel(filterModel);
      if (rowGroupCols.length > 0) api.setRowGroupColumns(rowGroupCols);

      if (!blob || typeof blob === "string") return;

      const file = new File([blob], `${activeTab}.xlsx`, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      let response;
      if (pathname.includes("/deliverable")) {
        response = await deliverableService.import<any>(
          deliverableId!,
          sheetId!,
          file,
          undefined,
          0,
          true,
        );
      } else if (pathname.includes("/template")) {
        response = await templateService.importTemplate(deliverableId!, file);
      } else if (pathname.includes("/coa")) {
        response = await coaService.importCoa(deliverableId!, file);
      }

      setIsEditMode(false);
      return response;
    },
    cancel: () => {
      setIsEditMode(false);
      message.success("Edit mode cancelled");
    },
    importFile: async (file: File) => {
      let response;
      if (pathname.includes("/deliverable")) {
        response = await deliverableService.import<any>(
          deliverableId!,
          sheetId!,
          file,
          undefined,
          0,
          false,
        );
      } else if (pathname.includes("/template")) {
        response = await templateService.importTemplate(deliverableId!, file);
      } else if (pathname.includes("/coa")) {
        response = await coaService.importCoa(deliverableId!, file);
      }
      return response;
    },
    exportFile: async () => {
      const downloadFile = (blob: Blob, fileName: string) => {
        const excelBlob = new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(excelBlob);
        const aElement = document.createElement("a");
        aElement.setAttribute("download", fileName);
        aElement.href = url;
        aElement.setAttribute("target", "_blank");
        aElement.click();
        URL.revokeObjectURL(url);
        message.success(`${activeTab} Downloaded Successful`);
      };
      try {
        const response = await deliverableService.exportAxios<any>(
          deliverableId!,
          sheetId!,
          undefined,
          undefined,
          0,
        );
        downloadFile(response, `${activeTab}.xlsx`);
      } catch (e: any) {
        message.error(e?.message || "Download Failed");
      }
    },
    addRow: (count: number) => {
      if (!gridApiRef.current) return;
      const newRows = createEmptyRows(gridApiRef.current, count);
      gridApiRef.current.applyTransaction({ add: newRows });
      message.success(`${count} Row(s) added successfully`);
    },
    refetchData: async () => {
      await fetchData();
    },
    search: (value: string) => {
      setSearchValue(value);
    },
  }));
};
